import type { Anchor } from "@rally/ui-kit";
import { Col, Input, Tappable, Text, useTheme, type ViewHandle } from "@rally/ui-kit";
import { useRef, useState } from "react";
import { say } from "../../localization/say.ts";
import { matching, SIX } from "./reacting.ts";

export function whereItIs(node: ViewHandle | null, then: (anchor: Anchor) => void) {
  node?.measureInWindow((x, y, width, height) => then({ x, y, width, height }));
}

export function Add({
  onPress,
  shown,
  testID,
}: {
  onPress: (at: Anchor) => void;
  shown: boolean;
  testID: string;
}) {
  const theme = useTheme();
  const box = useRef<ViewHandle | null>(null);
  return (
    <Tappable
      ref={box}
      onPress={() => whereItIs(box.current, onPress)}
      accessibilityRole="button"
      accessibilityLabel={say("react.add")}
      testID={testID}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        paddingHorizontal: 8,
        height: 24,
        borderRadius: theme.radius.round,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: theme.color["border.default"],
        opacity: shown ? 1 : 0,
      }}
      {...(shown ? {} : { "aria-hidden": true, pointerEvents: "none" as const })}
    >
      <Text style={{ fontSize: 12 }}>🙂</Text>
      <Text typeName="caption" color="text.tertiary">
        +
      </Text>
    </Tappable>
  );
}

export function ReactionPicker({
  onPick,
  onDismiss,
  testID,
}: {
  onPick: (emoji: string) => void;
  onDismiss: () => void;
  testID: string;
}) {
  const theme = useTheme();
  const [query, setQuery] = useState("");
  const found = matching(query);
  const pick = (emoji: string) => {
    onPick(emoji);
    onDismiss();
  };
  return (
    <Col
      testID={testID}
      style={{
        width: 240,
        gap: theme.space[2],
        padding: theme.space[3],
        borderRadius: theme.radius.md,
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        backgroundColor: theme.color["bg.elevated"],
      }}
    >
      <Col style={{ flexDirection: "row", gap: theme.space[2] }}>
        {SIX.map((emoji) => (
          <Tappable
            key={emoji}
            onPress={() => pick(emoji)}
            accessibilityRole="button"
            accessibilityLabel={emoji}
            testID={`react-pick-${emoji}`}
            style={{ padding: 4 }}
          >
            <Text style={{ fontSize: 20 }}>{emoji}</Text>
          </Tappable>
        ))}
      </Col>
      <Input
        value={query}
        onChangeText={setQuery}
        placeholder={say("react.search")}
        placeholderTextColor={theme.color["text.tertiary"]}
        testID="react-search"
        style={{
          height: 30,
          paddingHorizontal: theme.space[2],
          borderRadius: theme.radius.sm,
          borderWidth: 1,
          borderColor: theme.color["border.default"],
          color: theme.color["text.primary"],
        }}
      />
      {found.length > 0 ? (
        <Col style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space[2] }}>
          {found.map((emoji) => (
            <Tappable
              key={emoji}
              onPress={() => pick(emoji)}
              accessibilityRole="button"
              accessibilityLabel={emoji}
              testID={`react-found-${emoji}`}
              style={{ padding: 4 }}
            >
              <Text style={{ fontSize: 18 }}>{emoji}</Text>
            </Tappable>
          ))}
        </Col>
      ) : query.trim().length > 0 ? (
        <Text typeName="caption" color="text.tertiary">
          {say("react.noMatches")}
        </Text>
      ) : null}
    </Col>
  );
}
